import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageHeader from 'components/Header/Page';
import SectorFilter from 'components/Dropdown/SectorFilter';
import CustomDropdown from 'components/Dropdown';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { fetchSectorList } from 'service/service';
import {
    ENV_OPTIONS_EXTENDED,
    useAdbFilters,
    useAdbAnalysisTable,
    commonGenerateColumns,
    commonFetchSectors
} from 'utils/adb-common-util';
import PageWrapper from 'components/Common/PageWraper';

interface CustomAnalysisTableBaseProps {
    fetchService: any;
    columnMapping: Record<string, string>;
    defaultColumns: string[];
    filterTableDataFn: (data: any[], mapping: Record<string, string>) => any[];
    breadcrumbLabel: string;
    tableTitle: string | (() => string);
    tableType: string;
    extraParams?: Record<string, any>;
    preserveFilterKey: string;
    isAutoWidth?: boolean;
}

const CustomAnalysisTableBase: React.FC<CustomAnalysisTableBaseProps> = ({
    fetchService,
    columnMapping,
    defaultColumns,
    filterTableDataFn,
    breadcrumbLabel,
    tableTitle,
    tableType,
    extraParams = {},
    preserveFilterKey,
    isAutoWidth = false
}) => {
    const navigate = useNavigate();
    const {
        startDate, endDate, env, setEnv, sector, setSector, sectorName, setSectorName,
        location, selectedDateInString
    } = useAdbFilters(preserveFilterKey);

    const [sectorList, setSectorList] = useState<string[]>([]);

    useEffect(() => {
        commonFetchSectors(fetchSectorList, setSectorList);
    }, []);

    const filterTableData = useMemo(() => (data: any[]) => filterTableDataFn(data, columnMapping), [filterTableDataFn, columnMapping]);

    const filters = useMemo(() => ({
        startDate, endDate, env, sector, location, selectedDateInString
    }), [startDate, endDate, env, sector, location, selectedDateInString]);

    const {
        tableData,
        page,
        pageSize,
        sortingInfo,
        paginationData,
        loader,
        handlePageChange,
        handlePageSizeChange,
        handleSorting,
        handleExport
    } = useAdbAnalysisTable(fetchService, filters, filterTableData, extraParams);

    const columns = useMemo(() => commonGenerateColumns(columnMapping, { isAutoWidth }), [columnMapping, isAutoWidth]);

    const handleSectorChange = (value: string) => {
        setSector(value);
        setSectorName(value);
    };

    const backNavProps = {
        state: { startDate, endDate, env, sector: sectorName, location, selectedDateInString }
    };

    const resolvedTableTitle = typeof tableTitle === 'function' ? tableTitle() : tableTitle;

    return (
        <PageWrapper>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box sx={{ mb: 2, fontSize: '1.125rem', fontWeight: '600', color: '#003771', display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon
                        sx={{ color: 'black', cursor: 'pointer', mr: 2, ml: 2 }}
                        onClick={() => navigate('/databricksADB/customanalysis', backNavProps)}
                    />
                    <span style={{ cursor: 'pointer' }} onClick={() => navigate('/databricksADB/customanalysis', backNavProps)}>
                        Custom Analysis & Recommendations
                    </span>
                    <Box component="span" sx={{ mx: 1 }}>{'>'}</Box>
                    <span>{breadcrumbLabel}</span>
                </Box>
                <Box>
                    <PageHeader title=" " icon={''} lastSync={null}>
                        <CustomDropdown
                            handleChange={(event) => setEnv(event.target.value)}
                            value={env}
                            options={ENV_OPTIONS_EXTENDED}
                        />
                        <SectorFilter
                            sectorName={sectorName}
                            setSectorName={setSectorName}
                            sectorOptionsList={sectorList}
                            handleChange={handleSectorChange}
                        />
                    </PageHeader>
                </Box>
            </Stack>
            <CustomizedDataGrid
                title={resolvedTableTitle}
                tableColumns={columns}
                tableData={tableData}
                tableType={tableType}
                defaultColumnNames={defaultColumns}
                onSorting={handleSorting}
                isSortIcon={true}
                currSorting={sortingInfo}
                page={page}
                pageSize={pageSize}
                paginationData={paginationData}
                listLength={tableData.length}
                handlepageSizeChange={handlePageSizeChange}
                handlePageChange={handlePageChange}
                isDateRangeFilter={false}
                isDisabled={false}
                isFetching={loader}
                showExport={true}
                exportTableData={handleExport}
            />
        </PageWrapper>
    );
};

export default CustomAnalysisTableBase;
